// src/components/Admin/CategoryGroup/CategoryGroupFormModal.jsx
//
import React, { useState, useEffect, useRef } from 'react';
import { createCategoryGroup, updateCategoryGroup, addChildToGroup, removeChildFromGroup } from '../../../../services/categoryGroupApis';
import AppIconRenderer from '../../../Shared/AppIconRenderer'; // Import AppIconRenderer

// toProperCase and generateSlug functions remain the same...
// Modal component remains the same...

const toProperCase = (str) => { 
  if (!str) return '';
  return str.replace(/\b\w/g, char => char.toUpperCase()).replace(/_/g, ' ');
};

const generateSlug = (text) => { 
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const Modal = ({ isOpen, onClose, title, children }) => { 
    if (!isOpen) return null;
    return (
        <div
            className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <h2 id="modal-title" className="text-lg font-semibold text-gray-800">{title}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>
                <div className="p-6 space-y-6 overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>
    );
};


const CategoryGroupFormModal = ({ isOpen, onClose, group, allPurposes = [], allChildCategories = [], childCategoriesLoading, onGroupSaved, locale }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [currentChildCategories, setCurrentChildCategories] = useState([]);
  const [icon, setIcon] = useState(''); // New state for icon

  const [childCategorySearch, setChildCategorySearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchResultRefs = useRef([]); 

  const commonInputClassName = "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"; //
  console.log("Children:",allChildCategories)
  useEffect(() => {
    if (isOpen) {
        if (group) {
            const groupName = group.name || '';
            const groupSlug = group.slug || '';
            setName(groupName);
            setSlug(groupSlug);
            setDescription(group.description || '');
            setSelectedPurposes(group.purposes || []);
            setIsActive(group.is_active !== undefined ? group.is_active : true);
            setCurrentChildCategories(group.child_categories || []);
            setIcon(group.icon || ''); // Populate icon
            if (groupSlug && groupSlug === generateSlug(groupName)) {
                setSlugManuallyEdited(false);
            } else {
                setSlugManuallyEdited(!!groupSlug); 
            }
        } else { // New group
            setName('');
            setSlug('');
            setDescription('');
            setSelectedPurposes([]);
            setIsActive(true);
            setCurrentChildCategories([]);
            setChildCategorySearch('');
            setIcon(''); // Reset icon
            setSlugManuallyEdited(false); 
        }
        setError('');
        setHighlightedIndex(-1); 
    }
  }, [group, isOpen]);


  // handleNameChange, handleSlugChange, handlePurposeChange, 
  // handleAddChildCategory, handleRemoveChildCategory, 
  // filteredChildCategoriesForSelect, useEffect for highlight/scroll,
  // handleChildCategorySearchKeyDown remain the same as your latest file...

  const handleNameChange = (e) => { 
    const newName = e.target.value;
    setName(newName);
    if (!slugManuallyEdited) {
        setSlug(generateSlug(newName));
    }
  };

  const handleSlugChange = (e) => { 
    const newSlugValue = e.target.value;
    setSlug(newSlugValue); 
    setSlugManuallyEdited(true);
  };

  const handlePurposeChange = (purpose) => { 
    setSelectedPurposes(prev =>
      prev.includes(purpose) ? prev.filter(p => p !== purpose) : [...prev, purpose]
    );
  };

  const handleAddChildCategory = (childCat) => { 
    if (childCat && !currentChildCategories.find(cc => cc.id === childCat.id)) {
      // Ensure childCat.name is a string before adding
      const catName = typeof childCat.name === 'string' ? childCat.name : 'Invalid Category Name';
      // If you hit the "objects not valid as react child" error, console.log(childCat) here to inspect its structure.
      if (typeof childCat.name !== 'string') {
        console.warn("Warning: childCat.name is not a string:", childCat.name, "for childCat:", childCat);
      }
      setCurrentChildCategories(prev => [...prev, { id: childCat.id, name: catName, slug: childCat.slug }]);
    }
    setChildCategorySearch(''); 
    setHighlightedIndex(-1);
  };

  const handleRemoveChildCategory = (childCatId) => { 
    setCurrentChildCategories(prev => prev.filter(cc => cc.id !== childCatId));
  };

  const filteredChildCategoriesForSelect = allChildCategories.filter(cat => 
        typeof cat.name === 'string' && // Add safety check
        cat.name.toLowerCase().includes(childCategorySearch.toLowerCase()) &&
        !currentChildCategories.find(cc => cc.id === cat.id)
  ).slice(0, 10);

  useEffect(() => {
    setHighlightedIndex(-1); 
    searchResultRefs.current = searchResultRefs.current.slice(0, filteredChildCategoriesForSelect.length);
  }, [childCategorySearch, filteredChildCategoriesForSelect.length]);

  useEffect(() => {
    if (highlightedIndex >= 0 && searchResultRefs.current[highlightedIndex]) {
        searchResultRefs.current[highlightedIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest' 
        });
    }
  }, [highlightedIndex]);


  const handleChildCategorySearchKeyDown = (e) => {
    if (!filteredChildCategoriesForSelect.length && e.key !== 'Escape') return;

    switch (e.key) {
        case 'ArrowDown':
            e.preventDefault();
            setHighlightedIndex(prev => (prev + 1) % filteredChildCategoriesForSelect.length);
            break;
        case 'ArrowUp':
            e.preventDefault();
            setHighlightedIndex(prev => (prev - 1 + filteredChildCategoriesForSelect.length) % filteredChildCategoriesForSelect.length);
            break;
        case 'Enter':
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < filteredChildCategoriesForSelect.length) {
                handleAddChildCategory(filteredChildCategoriesForSelect[highlightedIndex]);
            }
            break;
        case 'Escape':
            e.preventDefault();
            setChildCategorySearch(''); 
            setHighlightedIndex(-1);    
            break;
        default:
            break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const finalSlug = generateSlug(slug);
    const groupDataToSubmit = {
      name, 
      slug: finalSlug, 
      description, 
      purposes: selectedPurposes, 
      is_active: isActive,
      icon: icon || null, // Add icon to payload
    };

    try {
      let savedGroupAPIResponse;
      if (group && group.id) { 
        const response = await updateCategoryGroup(group.id, groupDataToSubmit, locale);
        savedGroupAPIResponse = response.data;
      } else { 
        const response = await createCategoryGroup(groupDataToSubmit, locale);
        savedGroupAPIResponse = response.data;
      }
      if (savedGroupAPIResponse) {
        const originalChildrenIds = group ? (group.child_categories || []).map(c => c.id) : [];
        const currentChildrenIdsAsObjects = currentChildCategories;
        const childrenToAdd = currentChildrenIdsAsObjects.filter(cc => !originalChildrenIds.includes(cc.id));
        const childrenToRemoveIds = originalChildrenIds.filter(id => !currentChildrenIdsAsObjects.find(cc => cc.id === id));
        
        await Promise.all([
            ...childrenToAdd.map(child => addChildToGroup(savedGroupAPIResponse.id, child.id, locale)),
            ...childrenToRemoveIds.map(childId => removeChildFromGroup(savedGroupAPIResponse.id, childId, locale))
        ]);
        
        const finalGroupForState = {
            ...savedGroupAPIResponse, // This should include the icon from the backend response
            description: description, // Use form state if backend doesn't return it or if it's more current
            purposes: selectedPurposes,
            is_active: isActive,
            icon: icon || null, // Explicitly include the icon from form state
            child_categories: currentChildCategories 
        };
        if (onGroupSaved) {
            onGroupSaved(finalGroupForState);
        }
      }
      onClose();
    } catch (err) {
      // Error handling remains the same as your latest file...
      console.error("Failed to save category group:", err); 
      let displayError = 'An unexpected error occurred. Please try again.'; 
      if (err.response) {
        if (err.response.data) {
          if (typeof err.response.data.detail === 'string') {
            displayError = err.response.data.detail;
          } else if (typeof err.response.data.message === 'string') {
            displayError = err.response.data.message;
          } else if (typeof err.response.data === 'string') { 
            displayError = err.response.data;
          } else if (err.response.data.error && typeof err.response.data.error === 'string') { 
            displayError = err.response.data.error;
          }
        }
      } else if (err.message && typeof err.message === 'string') { 
        displayError = err.message;
      } else if (typeof err === 'string') { 
        displayError = err;
      }
      console.log("Setting error display message to:", displayError); 
      setError(displayError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={group ? 'Edit Category Group' : 'Add New Category Group'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
            <div className="p-3 bg-red-100 border border-red-300 rounded-md my-4"> {/* Adjusted error style slightly */}
                <p className="text-sm text-red-700">{error}</p>
            </div>
        )}
        
        <div>
          <label htmlFor="groupName" className="block text-sm font-medium text-gray-700">Name</label>
          <input type="text" id="groupName" value={name} onChange={handleNameChange} required className={commonInputClassName} />
        </div>

        {/* New Icon Input Field - styled similar to InfoTab.jsx */}
        <div>
          <label htmlFor="groupIcon" className="block text-sm font-medium text-gray-700">
            Group Icon Name
          </label>
          <div className="mt-1 flex items-center space-x-2">
            <input
              type="text"
              id="groupIcon"
              value={icon}
              onChange={e => setIcon(e.target.value)}
              placeholder="e.g., home (Lucide), HeartBold (Phosphor)"
              className={`${commonInputClassName} flex-1`}
            />
            {icon && (
              <div className="p-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center h-10 w-10"> {/* Consistent with InfoTab preview box */}
                <AppIconRenderer 
                  name={icon} 
                  size={20} 
                  className="text-gray-600" // Adjusted color slightly
                />
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500"> {/* Help text similar to InfoTab */}
            Enter icon name from <a href="https://lucide.dev/icons/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">Lucide</a> or 
            <a href="https://phosphoricons.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline"> Phosphor</a>.
          </p>
        </div>

        <div>
          <label htmlFor="groupSlug" className="block text-sm font-medium text-gray-700">Slug</label>
          <input
            type="text" id="groupSlug" value={slug} onChange={handleSlugChange}
            onBlur={() => setSlug(generateSlug(slug))} required className={commonInputClassName}
          />
           {!slugManuallyEdited && name && <p className="mt-1 text-xs text-gray-500">Auto-generated from name. Edit to customize.</p>}
           {slugManuallyEdited && <p className="mt-1 text-xs text-indigo-500">Slug has been manually edited.</p>}
        </div>
        
        <div>
          <label htmlFor="groupDescription" className="block text-sm font-medium text-gray-700">Description</label>
          <textarea id="groupDescription" value={description} onChange={e => setDescription(e.target.value)} rows="3" className={commonInputClassName}></textarea>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Purposes</label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
            {allPurposes.map(purpose => (
              <label key={purpose} className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox" checked={selectedPurposes.includes(purpose)}
                  onChange={() => handlePurposeChange(purpose)} 
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span>{toProperCase(purpose)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Child Categories Search Section remains the same as your latest file... */}
        <div className="space-y-2">
            <label htmlFor="childCategorySearch" className="block text-sm font-medium text-gray-700">Child Categories</label>
            <div className="relative">
                <input
                    type="text"
                    id="childCategorySearch"
                    placeholder="Search and add child category..."
                    value={childCategorySearch}
                    onChange={e => {
                        setChildCategorySearch(e.target.value);
                    }}
                    onKeyDown={handleChildCategorySearchKeyDown} 
                    className={commonInputClassName} 
                    autoComplete="off" 
                />
                {childCategoriesLoading && <p className="mt-1 text-xs text-gray-500">Loading categories...</p>}
                {childCategorySearch && filteredChildCategoriesForSelect.length > 0 && !childCategoriesLoading && (
                    <ul 
                        className="absolute z-10 w-full mt-1 list-none bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
                        role="listbox" 
                    >
                        {filteredChildCategoriesForSelect.map((cat, index) => (
                            <li
                                key={cat.id}
                                ref={el => searchResultRefs.current[index] = el} 
                                onClick={() => handleAddChildCategory(cat)}
                                onMouseEnter={() => setHighlightedIndex(index)} 
                                className={`px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-indigo-50 transition-colors 
                                            ${index === highlightedIndex ? 'bg-indigo-100 ring-1 ring-indigo-500' : ''}`} 
                                role="option"
                                aria-selected={index === highlightedIndex} 
                            >
                                {typeof cat.name === 'string' ? cat.name : 'Error: Name is object'} {/* Safety for "Objects not valid" error */}
                            </li>
                        ))}
                    </ul>
                )}
                 {childCategorySearch && !childCategoriesLoading && filteredChildCategoriesForSelect.length === 0 && (
                     <p className="mt-1 text-xs text-gray-500">No matching categories found.</p>
                 )}
            </div>

            {currentChildCategories.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {currentChildCategories.map(cc => (
                        <span key={cc.id} className="flex items-center bg-indigo-100 text-indigo-700 text-xs font-medium px-2.5 py-1 rounded-full">
                            {typeof cc.name === 'string' ? cc.name : 'Error: Name is object'} {/* Safety for "Objects not valid" error */}
                            <button
                                type="button"
                                onClick={() => handleRemoveChildCategory(cc.id)}
                                className="ml-1.5 text-indigo-500 hover:text-indigo-700 focus:outline-none"
                                aria-label={`Remove ${typeof cc.name === 'string' ? cc.name : 'item'}`}
                            >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
        
        <div className="flex items-center">
          <input
            id="groupIsActive" type="checkbox" checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label htmlFor="groupIsActive" className="ml-2 block text-sm text-gray-900">Active</label>
        </div>
        
        {/* Buttons remain the same */}
        <div className="pt-2 flex flex-col sm:flex-row sm:justify-end sm:space-x-3">
            <button
                type="button" onClick={onClose} disabled={loading}
                className="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 mb-2 sm:mb-0"
            >Cancel</button>
            <button
                type="submit" disabled={loading || !name || !slug} 
                className="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:bg-green-400"
            >{loading ? 'Saving...' : (group ? 'Update Group' : 'Create Group')}</button>
        </div>
      </form>
    </Modal>
  );
};
export default CategoryGroupFormModal;